import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

const TrueWellnessFAQ = () => {
  const faqs = [
    {
      question: "Why avoid TrumpRx due to insurance or program restrictions?",
      answer: "TrumpRx is cash-pay only and excludes those enrolled in insurance, Medicare, Medicaid, or federal programs—limiting access for many.",
      hillaryRxSolution: "No insurance needed! Our telehealth model welcomes all eligible patients for compounded semaglutide or tirzepatide starting at $74/mo—fair access without exclusions."
    },
    {
      question: "Why skip TrumpRx if prices are still too high?",
      answer: "Even discounted, TrumpRx starts at $149–$350/mo (often higher for full doses), which exceeds $100/mo affordability thresholds for some.",
      hillaryRxSolution: "Starters from $74/mo for compounded GLP-1 weight loss—bundled with consults and shipping. No drama, just value."
    },
    {
      question: "Why not use TrumpRx for pure weight loss?",
      answer: "Medicare/Medicaid won't cover GLP-1s solely for obesity—only diabetes or heart issues—leaving gaps.",
      hillaryRxSolution: "Doctor-prescribed compounded options for weight loss GLP-1 like tirzepatide ($149/mo equiv.)—equitable for all qualifying via virtual consult."
    },
    {
      question: "Why avoid TrumpRx without bundled support?",
      answer: "TrumpRx focuses on meds only—no telehealth, follow-ups, or side effect management included.",
      hillaryRxSolution: "All-in plans with virtual provider consults, shipping, and ongoing support for semaglutide weight loss—your sassy wellness ally."
    },
    {
      question: "Why opt out of TrumpRx over dose limitations?",
      answer: "Discounts apply mainly to starters; higher/maintenance doses cost more, with murky details.",
      hillaryRxSolution: "Flexible 3-month programs up to 15mg tirzepatide or 2.5mg semaglutide from $111/mo—customized without surprises."
    },
    {
      question: "Why avoid TrumpRx for political or privacy reasons?",
      answer: "Some skip due to associations or government data concerns.",
      hillaryRxSolution: "Independent, no-ties-to-big-pharma—or politics. Get fair GLP-1 weight loss—empowering without the weight."
    },
  ];

  // Generate FAQ Schema for rich snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": `${faq.answer} HillaryRx alternative: ${faq.hillaryRxSolution}`
      }
    }))
  };

  return (
    <section id="faq" className="py-16 md:py-24 bg-[#181818]">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-4 py-2 bg-emerald-500/10 text-emerald-400 font-semibold rounded-full text-sm mb-4">
              TrumpRx GLP-1 FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
              Top Reasons People Skip It & <span className="text-emerald-400">HillaryRx Alternatives</span>
            </h2>
            <p className="text-lg sm:text-xl text-emerald-100/70 max-w-2xl mx-auto px-4">
              Navigating TrumpRx weight loss GLP-1 options like semaglutide (Ozempic, Wegovy) or tirzepatide (Zepbound)? Here's an FAQ on common reasons people opt out.
            </p>
          </div>

          {/* Safety Note */}
          <Alert className="bg-amber-500/10 border-amber-500/30 mb-8">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
            <AlertDescription className="text-amber-200/90 text-sm ml-2">
              <span className="font-semibold text-amber-300">Important:</span> Consult a provider; compounded meds aren't FDA-approved; results vary; side effects possible (e.g., nausea). These statements have not been evaluated by the FDA. Peptide protocols are compounded by licensed pharmacies and prescribed by licensed providers based on individual assessment. Not intended to diagnose, treat, cure, or prevent any disease.
            </AlertDescription>
          </Alert>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-emerald-900/30 border border-emerald-700/30 rounded-xl px-4 sm:px-6 relative z-10 backdrop-blur-sm"
              >
                <AccordionTrigger className="text-left font-semibold text-sm sm:text-base text-white hover:text-emerald-400 hover:no-underline cursor-pointer py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-emerald-100/80 leading-relaxed pb-5">
                  <p className="mb-4">{faq.answer}</p>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <p className="text-emerald-200">
                      <span className="font-bold text-emerald-400">HillaryRx Alternative:</span> {faq.hillaryRxSolution}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default TrueWellnessFAQ;
